import * as React from "react";
import { useFormContext } from "react-hook-form";
import TLXMultiSelect, { toSelectDataArray } from "@/components/MultiSelect";
import type { SelectData } from "./FormDropDownList";
import { useCoerceToArrayValue } from "./useCoerceToArrayValue";
import { useControlledField, type FieldValidator } from "./useControlledField";

/**
 * RHF wrapper around TLXMultiSelect. TLXMultiSelect itself accepts a Kendo Form
 * FieldRenderProps shape; we synthesize the value/onChange pair from useController
 * so the public API of FormMultiSelect is RHF-native.
 */
type TLXMultiSelectProps = React.ComponentProps<typeof TLXMultiSelect>;

export interface FormMultiSelectProps
  extends Omit<TLXMultiSelectProps, "name" | "value" | "onChange"> {
  name: string;
  validator?: FieldValidator;
  /**
   * Optional side-effect callback after this field's RHF value is updated by a
   * **user** changing the selection.
   *
   * **When to use**
   * - Cascade updates to *other* form fields when the user adds/removes items.
   * - Port legacy Kendo `<Field component={FormMultiSelect} onChange={...}>`
   *   handlers into RHF slide-panel forms.
   *
   * **When to avoid**
   * - Do not mirror this field's value via `useEffect` + `useWatch` and then call
   *   `formApi.onChange` — that pattern can cause infinite update loops.
   * - Do not use for API-driven or edit-mode initial hydration; use
   *   `formApi.setProgrammaticValue` / `setProgrammaticValues` instead.
   * - Do not write *this* field's value here; RHF is already updated internally.
   *
   * **Not called** on mount, auto-prune no-ops (data load with unchanged keys),
   * or any programmatic `setValue` / `reset`.
   */
  onSelectionChange?: (value: unknown) => void;
}

// Compare two SelectData[] arrays structurally by the field's identity key.
// TLXMultiSelect's auto-prune effect (MultiSelect/index.tsx:233) emits an
// onChange whenever its data prop loads — even when the pruned value equals
// the current value. Without this guard, RHF treats that emission as a
// user-edit and marks the field dirty, which trips the wizard's close guard
// the first time the user lands on a step in edit mode.
const sameByKey = (
  a: SelectData[] | undefined,
  b: SelectData[] | undefined,
  dataItemKey: string = "id",
): boolean => {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  const key = dataItemKey as keyof SelectData;
  for (let i = 0; i < aa.length; i++) {
    if (aa[i]?.[key] !== bb[i]?.[key]) return false;
  }
  return true;
};

export const FormMultiSelect: React.FC<FormMultiSelectProps> = ({
  name,
  validator,
  onSelectionChange,
  ...rest
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const methods = useFormContext();
  const value = toSelectDataArray(field.value);
  const dataItemKey = (rest as { dataItemKey?: string }).dataItemKey ?? "id";

  useCoerceToArrayValue(name, field.value);

  return (
    <TLXMultiSelect
      {...rest}
      name={name}
      value={value}
      onChange={(event: { target?: unknown; value?: unknown }) => {
        const next = (event.value as SelectData[] | undefined) ?? [];
        if (sameByKey(value, next, dataItemKey)) {
          // Auto-prune emitted a no-op — apply silently so the field
          // does not get marked dirty without a user interaction.
          methods.setValue(name, next as never, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: !!fieldState.error,
          });
          return;
        }
        methods.setValue(name, next as never, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        onSelectionChange?.(next);
      }}
      onBlur={field.onBlur}
      valid={!fieldState.error}
      validationMessage={fieldState.error?.message}
      touched={fieldState.isTouched}
    />
  );
};

export default FormMultiSelect;
